import { useState, useEffect, useMemo } from 'react';
import { Typography, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import Loader from '@shared/components/Loader';
import BusinessMetricsSection, {
  ServiceDetailedMetricsSection,
} from '../features/finance/pricing-calculator/components/BusinessMetricsSection';
import FinancialCostsForm from '../features/finance/pricing-calculator/components/FinancialCostsForm';
import { OverheadCost } from '../features/finance/pricing-calculator/components/MonthlyTrendChart';
import MonthSelector from '../features/finance/pricing-calculator/components/MonthSelector';
import {
  useCurrentPricingCalculatorQuery,
  useCustomerRetentionByMonthQuery,
} from '../features/finance/pricing-calculator/queries';
import { useVisitsQuery } from '../features/visits/queries';

export type CreatePricingCalculatorInput = {
  overheadCosts: OverheadCost[];
  calculationMonth?: Date;
};

export type FinancialOutcome = {
  costs: OverheadCost[];
};

const PricingCalculator = () => {
  const [selectedMonth, setSelectedMonth] = useState<Date>(dayjs().toDate());
  const { data: existingCalculator, isLoading } = useCurrentPricingCalculatorQuery(selectedMonth);
  const { data: clientsRetention } = useCustomerRetentionByMonthQuery(dayjs(selectedMonth));

  const previousMonth = useMemo(() => {
    return dayjs(selectedMonth).subtract(1, 'month').toDate();
  }, [selectedMonth]);

  const { data: previousMonthCalculator } = useCurrentPricingCalculatorQuery(previousMonth);
  const startOfMonth = useMemo(() => dayjs(selectedMonth).startOf('month'), [selectedMonth]);
  const endOfMonth = useMemo(() => dayjs(selectedMonth).endOf('month'), [selectedMonth]);

  const { data: visits } = useVisitsQuery({
    query: { from: startOfMonth, to: endOfMonth },
  });
  const dateRange = {
    from: startOfMonth.toDate(),
    to: endOfMonth.toDate(),
  };
  const costs = existingCalculator?.overheadCosts || [];

  const { control, reset, getValues } = useForm<FinancialOutcome>({
    defaultValues: {
      costs: [{ name: '', amount: 0, period: 'monthly', description: '' }],
    },
  });

  const handleCopyFromPreviousMonth = () => {
    if (previousMonthCalculator) {
      reset({
        costs:
          previousMonthCalculator.overheadCosts.length > 0
            ? previousMonthCalculator.overheadCosts
            : [{ name: '', amount: 0, period: 'monthly', description: '' }],
      });
    }
  };

  useEffect(() => {
    const calculator = existingCalculator || previousMonthCalculator;
    if (calculator) {
      reset({
        costs:
          calculator.overheadCosts.length > 0
            ? calculator.overheadCosts
            : [{ name: '', amount: 0, period: 'monthly', description: '' }],
      });
    } else {
      reset({
        costs: [{ name: '', amount: 0, period: 'monthly', description: '' }],
      });
    }
  }, [existingCalculator, previousMonthCalculator, reset, selectedMonth]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">
            <FormattedMessage id="pricing.costs.title" defaultMessage="Finance" />
          </Typography>
          <MonthSelector selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />
        </Stack>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          <FormattedMessage
            id="pricing.costs.subtitle"
            defaultMessage="Každý měsíc/rok máte v podnikání pravidelné výdaje mimo nákladů na materiál. Například nájem, vaše výplata, tvoření úspor na dovolenou, reklama, školení atd. Zadejte je do této tabulky k danému měsíci. Stiskněte vypočítat a uložit a koukněte zda máte správně nastavené ceny."
          />
        </Typography>
        <FinancialCostsForm
          control={control}
          month={selectedMonth}
          getValues={getValues}
          lastUpdate={existingCalculator?.updatedAt}
          onCopyFromPreviousMonth={handleCopyFromPreviousMonth}
          hasPreviousMonthData={!!previousMonthCalculator}
        />
      </Stack>

      <BusinessMetricsSection
        clientsRetention={clientsRetention}
        visits={visits || []}
        overheadCosts={costs}
        dateRange={dateRange}
      />
      <ServiceDetailedMetricsSection
        visits={visits || []}
        overheadCosts={costs}
        dateRange={dateRange}
      />
    </Stack>
  );
};

export default PricingCalculator;
